import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Switch,
    Checkbox,
    FormControlLabel,
    Button,
    Box,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SiteUser, User } from "../../types";
import { DesktopDatePicker } from "@mui/lab";
import theme from "../../../../app/theme";

interface EditUserModalProps {
    open: boolean;
    user: User | SiteUser | null;
    onClose: () => void;
    onSave: (user: User | SiteUser) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ open, user, onClose, onSave }) => {
    const [form, setForm] = useState<User | SiteUser | null>(user);
    useEffect(() => {
        setForm(user);
    }, [user]);

    if (!form) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleChangeDate = (date: Date | null) => {
        if (!date) return;
        setForm({ ...form!, accessExpires: date });
    };

    const handleSwitch = (_: any, checked: boolean) => {
        setForm({ ...form!, activated: checked });
    };

    const isSiteUser = (u: User | SiteUser): u is SiteUser => "sites" in u;

    const handleCertificationChange = (_: any, checked: boolean) => {
        if (!form || !isSiteUser(form)) return;
        setForm({ ...form, certificationRequired: checked });
    };

    const handleSave = () => {
        if (form) {
            onSave(form);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">{"Edit User"}</Typography>
                        <Typography variant="body2" color="textSecondary">
                            {"Update user information"}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pt: 4, pb: 3 }}>
                {/* Dummy inputs to fool browser autofill */}
                <Box sx={{ width: 0, height: 0, overflow: "hidden" }}>
                    <input type="text" name="fakeusernameremembered" tabIndex={-1} />
                    <input type="password" name="fakepasswordremembered" tabIndex={-1} />
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap" mb={20} mt={20}>
                    <TextField
                        label="Email"
                        name="email"
                        value={form.email}
                        inputProps={{
                            autoComplete: "new-password",
                            readOnly: true,
                            sx: { color: "#4caf50", fontWeight: 500 },
                        }}
                        fullWidth
                        disabled
                        size="small"
                        sx={{
                            flex: 1,
                            bgcolor: theme.palette.background.paper,
                            input: { color: "#4caf50" },
                            label: { color: "#aaa" },
                            marginRight: 5,
                        }}
                    />
                    <TextField
                        label="First Name"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        inputProps={{ autoComplete: "new-password" }}
                        sx={{
                            flex: 1,
                            bgcolor: theme.palette.background.paper,
                            input: { color: "#fff" },
                            label: { color: "#aaa" },
                            marginLeft: 5,
                        }}
                    />
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap" mb={10}>
                    <TextField
                        label="Last Name"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        fullWidth
                        inputProps={{ autoComplete: "new-password" }}
                        size="small"
                        sx={{
                            flex: 1,
                            bgcolor: theme.palette.background.paper,
                            input: { color: "#fff" },
                            label: { color: "#aaa" },
                            marginRight: 5,
                        }}
                    />
                    <DesktopDatePicker
                        value={form.accessExpires}
                        onChange={(newDate: Date | null) => handleChangeDate(newDate)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Access Expires"
                                name="expiresOn"
                                fullWidth
                                size="small"
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: "new-password",
                                    readOnly: true,
                                }}
                                sx={{
                                    flex: 1,
                                    bgcolor: theme.palette.background.paper,
                                    input: { color: "#fff" },
                                    label: { color: "#aaa" },
                                    marginLeft: 5,
                                }}
                                autoFocus
                            />
                        )}
                    />
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap" mb={10}>
                    <Box sx={{ flex: 1, marginRight: 5 }}>
                        <FormControlLabel
                            control={<Switch checked={form.activated} onChange={handleSwitch} color="success" />}
                            label="Activated"
                            sx={{ color: "#fff" }}
                        />
                    </Box>
                    {isSiteUser(form) && (
                        <Box sx={{ flex: 1, marginLeft: 5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!form.certificationRequired}
                                        onChange={handleCertificationChange}
                                        sx={{
                                            "color": "#aaa",
                                            "&.Mui-checked": { color: "#4caf50" },
                                        }}
                                    />
                                }
                                label="Requires certification"
                                sx={{ color: "#fff" }}
                            />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.background.paper, py: 16, px: 24 }}>
                <Button onClick={onClose} sx={{ mr: 2 }}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave}>
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditUserModal;
